import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Avatar, Modal, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from '@mui/material';
import { primaryColor } from '../theme';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import moment from 'moment';

const roles = {'Head Office Admin': ['Head Office Admin', 'Regional Manager', 'Content Editor', 'Content Reviewer', 'Trainer'], 'Regional Manager': ['Content Editor', 'Content Reviewer', 'Trainer']};

const UserDetails = () => {
  const [error, setError] = useState({});
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [userLocation, setUserLocation] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentData, setContentData] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setUserRole(user.role);
      setUserLocation(user.location);
      setAllowedRoles(roles[user.role] || []);
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/${id}`);
        setUser(response.data);
        setUpdatedUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUser();
  }, [id]);

  useEffect(() => {
    if (user && user.role === 'Trainer') {
      const fetchContentData = async () => {
        setLoadingContent(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user-content-activity/${user.id}`);
          setContentData(response.data);
          setLoadingContent(false);
        } catch (error) {
          console.error('Error fetching content data:', error);
          setLoadingContent(false);
        }
      };

      fetchContentData();
    }
  }, [user]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({ ...updatedUser, [name]: value });
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setUpdatedUser({ ...updatedUser, profilePicture: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateUser = async () => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/${id}`, updatedUser);
      setUser(response.data);
      setOpenEdit(false);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  const handleOpenConfirm = (action) => {
    setConfirmAction(action);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleConfirmAction = async () => {
    try {
      if (confirmAction === 'delete') {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/users/${id}`);
        navigate('/users');
      } else {
        const status = confirmAction === 'activate' ? 'Active' : 'Suspended';
        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/${id}`, { status });
        setUser(response.data);
      }
      setOpenConfirm(false);
    } catch (error) {
      console.error(`Error ${confirmAction} user:`, error);
    }
  };

  const columns = [
    { name: 'No.', selector: (row, index) => (currentPage - 1) * perPage + index + 1, width: '60px' },
    { name: 'Topic', selector: row => row.content.topic, sortable: true },
    { name: 'Platform', selector: row => row.content.platform, sortable: true },
    { name: 'Last View Date', selector: row => row.lastViewed ? moment(row.lastViewed).format('DD-MM-YYYY, h:mm a') : 'N/A', sortable: true },
    { name: 'Last Download Date', selector: row => row.lastDownloaded ? moment(row.lastDownloaded).format('DD-MM-YYYY, h:mm a') : 'N/A', sortable: true },
    { name: 'No. of Views', selector: row => row.viewCount, sortable: true },
    { name: 'No. of Downloads', selector: row => row.downloadCount, sortable: true },
  ];


  if (userRole !== 'Head Office Admin' && userRole !== 'Regional Manager') {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">
          You do not have permission to add new users.
        </Typography>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">User not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom style={{ color: primaryColor }}>
        User Details
      </Typography>
      <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
        <Avatar src={user.profilePicture} sx={{ width: 80, height: 80, mb: 2 }} />
        <Typography variant="body1"><strong>Name:</strong> {user.firstName} {user.lastName}</Typography>
        <Typography variant="body1"><strong>Contact:</strong> {user.contact}</Typography>
        <Typography variant="body1"><strong>Email:</strong> {user.email}</Typography>
        <Typography variant="body1"><strong>Role:</strong> {user.role}</Typography>
        <Typography variant="body1"><strong>Location:</strong> {user.location}</Typography>
        <Typography variant="body1"><strong>Status:</strong> {user.status}</Typography>
        <Typography variant="body1"><strong>Date Registered:</strong> {moment(user.dateCreated).format('MMMM Do YYYY, h:mm a')}</Typography>
        <Typography variant="body1"><strong>Last Active Date:</strong> {user.lastLogin ? moment(user.lastLogin).format('MMMM Do YYYY, h:mm a') : 'Never logged in'}</Typography>
      </Box>
      <Box mt={2} display="flex" justifyContent="center">
        <Button variant="contained" color="primary" onClick={handleOpenEdit} sx={{ backgroundColor: primaryColor, mr: 2 }}>
          Edit Details
        </Button>
        <Button variant="contained" color="secondary" onClick={() => navigate('/users')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Button variant="contained" color="error" onClick={() => handleOpenConfirm('delete')} sx={{ mr: 2 }}>
          Delete
        </Button>
        <Button variant="contained" color="warning" onClick={() => handleOpenConfirm(user.status === 'Active' ? 'suspend' : 'activate')}>
          {user.status === 'Active' ? 'Suspend' : 'Activate'}
        </Button>
      </Box>
      {user.role === 'Trainer' && (
        <Box mt={4} mb={2}>
          <Typography variant="h6" gutterBottom>
            User Content Activity
          </Typography>
          {loadingContent ? (
            <CircularProgress />
          ) : (
            <DataTable
              columns={columns}
              data={contentData}
              pagination
              paginationPerPage={perPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              highlightOnHover
              pointerOnHover
              responsive
              customStyles={{
                headCells: {
                  style: {
                    backgroundColor: primaryColor,
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  },
                },
                cells: {
                  style: {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  },
                },
              }}
            />
          )}
        </Box>
      )}
      <Modal open={openEdit} onClose={handleCloseEdit}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom>
            Edit User Details
          </Typography>
          <TextField
            label="First Name"
            name="firstName"
            value={updatedUser.firstName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={updatedUser.lastName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Contact"
            name="contact"
            value={updatedUser.contact}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={updatedUser.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal" error={!!error.role}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              name="role"
              value={updatedUser.role}
              onChange={handleInputChange}
            >
              {allowedRoles.map(role => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
            {error.role && <Typography variant="body2" color="error">{error.role}</Typography>}
          </FormControl>
          {userRole === 'Head Office Admin' ? (
            <TextField
              label="Region"
              variant="outlined"
              fullWidth
              margin="normal"
              name="location"
              value={updatedUser.location}
              onChange={handleInputChange}
              error={!!error.location}
              helperText={error.location}
            />                                                            
          ) : (
            <TextField
              label="Region"
              variant="outlined"
              fullWidth
              margin="normal"
              name="location"
              value={userLocation}
              onChange={handleInputChange}
              error={!!error.location}
              helperText={error.location}
            />
          )}
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          >
            Upload Profile Picture
            <input
              type="file"
              hidden
              onChange={handleProfilePictureChange}
            />
          </Button>
          {selectedFileName && (
            <Typography variant="body2" color="textSecondary" align="center">
              Selected file: {selectedFileName}
            </Typography>
          )}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button variant="contained" color="primary" onClick={handleUpdateUser} sx={{ backgroundColor: primaryColor }}>
              Update
            </Button>
            <Button variant="contained" color="secondary" onClick={handleCloseEdit}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
      >
        <DialogTitle>Confirm {confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmAction} this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetails;